import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Wallet, Plus, RefreshCw, Trash2 } from "lucide-react";

/**
 * Phase 73 · R3 — Recruitment Cost Tracker
 *
 * Both global (org-wide monthly buckets — LinkedIn seat, tool subs) and
 * per-job / per-client line items (job-board postings, agency fees).
 * Feeds cost-per-hire and ROI dashboards downstream.
 */
const CATEGORIES = ["Job board posting", "Ad spend", "Tool subscription", "Agency fee", "Referral bonus", "Interviewer time", "Travel", "Other"];

export default function CostsPage() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [filter, setFilter] = useState({ scope: "", from: "", to: "" });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter.scope) params.scope = filter.scope;
            if (filter.from) params.period_from = filter.from;
            if (filter.to) params.period_to = filter.to;
            const { data } = await api.get("/costs", { params });
            setRows(data?.items || []); setTotal(data?.total || 0);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const remove = async (id) => {
        if (!confirm("Delete this cost line?")) return;
        try { await api.delete(`/costs/${id}`); toast.success("Deleted"); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="max-w-6xl mx-auto p-3 sm:p-6 lg:p-8" data-testid="costs-page">
            <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Reporting · Cost tracker</div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-1">Recruitment costs</h1>
                    <p className="text-muted-foreground text-sm mt-2 max-w-2xl">Track every dollar spent on hiring — job-board posts, ad spend, tool subs, agency fees. Powers cost-per-hire and source ROI.</p>
                </div>
                <Button onClick={() => setCreating(true)} className="rounded-none h-9 bg-ikb hover:bg-[#001f7a] text-white" data-testid="cost-add"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add cost line</Button>
            </div>

            <div className="border border-border bg-white">
                <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border flex-wrap">
                    <select value={filter.scope} onChange={(e) => setFilter({ ...filter, scope: e.target.value })} className="rounded-none h-8 border border-border px-2 text-sm bg-white" data-testid="cost-filter-scope">
                        <option value="">All scopes</option>
                        <option value="global">Global</option>
                        <option value="job">Per job</option>
                        <option value="client">Per client</option>
                    </select>
                    <Input type="date" value={filter.from} onChange={(e) => setFilter({ ...filter, from: e.target.value })} className="rounded-none h-8 w-40" />
                    <span className="text-xs text-muted-foreground">→</span>
                    <Input type="date" value={filter.to} onChange={(e) => setFilter({ ...filter, to: e.target.value })} className="rounded-none h-8 w-40" />
                    <div className="ml-auto flex items-center gap-3">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Total: <span className="text-foreground font-bold">${total.toLocaleString()}</span></div>
                        <Button variant="outline" onClick={load} disabled={loading} className="rounded-none h-8"><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /></Button>
                    </div>
                </div>
                {loading ? (
                    <div className="p-8 text-sm text-muted-foreground text-center">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-sm text-muted-foreground">
                        <Wallet className="w-8 h-8 mx-auto text-muted-foreground/40" />
                        <div className="mt-3 font-mono uppercase tracking-widest">No costs recorded yet</div>
                    </div>
                ) : (
                    <div className="overflow-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/40 text-[10px] font-mono uppercase tracking-widest">
                                <tr>
                                    <th className="px-3 py-2 text-left">Category</th>
                                    <th className="px-3 py-2 text-left">Scope</th>
                                    <th className="px-3 py-2 text-left">Period</th>
                                    <th className="px-3 py-2 text-left">Description</th>
                                    <th className="px-3 py-2 text-right">Amount</th>
                                    <th className="px-3 py-2 w-8"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {rows.map((r) => (
                                    <tr key={r.id} className="hover:bg-secondary/30" data-testid={`cost-row-${r.id}`}>
                                        <td className="px-3 py-2">{r.category}</td>
                                        <td className="px-3 py-2 text-xs">
                                            <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-border">{r.scope}</span>
                                        </td>
                                        <td className="px-3 py-2 text-xs font-mono">{(r.period_start || "").slice(0, 10)} → {(r.period_end || "").slice(0, 10)}</td>
                                        <td className="px-3 py-2 text-xs">{r.description || "—"}</td>
                                        <td className="px-3 py-2 text-right font-mono">${Number(r.amount).toLocaleString()}</td>
                                        <td className="px-3 py-2"><button onClick={() => remove(r.id)} className="text-rose-600 hover:text-rose-800" title="Delete" data-testid={`cost-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {creating && <CreateCostDialog onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
        </div>
    );
}

function CreateCostDialog({ onClose, onDone }) {
    const [form, setForm] = useState({
        scope: "global", scope_id: "", category: CATEGORIES[0], amount: "",
        currency: "USD", period_start: "", period_end: "", description: "",
    });
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!form.amount || !form.period_start || !form.period_end) { toast.error("Amount and period required"); return; }
        if (form.scope !== "global" && !form.scope_id) { toast.error(`${form.scope}_id required`); return; }
        setBusy(true);
        try {
            await api.post("/costs", { ...form, amount: parseFloat(form.amount), scope_id: form.scope_id || null });
            toast.success("Cost recorded"); onDone();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-none max-w-md" data-testid="cost-create-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display font-bold text-xl">Add cost line</DialogTitle>
                    <DialogDescription className="sr-only">Log a recruitment cost — global (org-wide) or scoped to a specific job or client.</DialogDescription>
                </DialogHeader>
                <div className="space-y-2.5">
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Scope</span>
                            <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value, scope_id: "" })} className="mt-1 w-full h-10 border border-input px-2 text-sm bg-background">
                                <option value="global">Global</option>
                                <option value="job">Per job</option>
                                <option value="client">Per client</option>
                            </select>
                        </label>
                        {form.scope !== "global" && (
                            <label className="block">
                                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{form.scope === "job" ? "Job ID" : "Client ID"}</span>
                                <Input value={form.scope_id} onChange={(e) => setForm({ ...form, scope_id: e.target.value })} className="rounded-none mt-1" data-testid="cost-scope-id" />
                            </label>
                        )}
                    </div>
                    <label className="block">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Category</span>
                        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full h-10 border border-input px-2 text-sm bg-background">
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Amount</span>
                            <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-none mt-1" data-testid="cost-amount" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Currency</span>
                            <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="rounded-none mt-1" />
                        </label>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Period start</span>
                            <Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} className="rounded-none mt-1" data-testid="cost-from" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Period end</span>
                            <Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="rounded-none mt-1" data-testid="cost-to" />
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Description</span>
                        <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none mt-1" placeholder="LinkedIn Recruiter seat · Q3 2026" data-testid="cost-desc" />
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="cost-submit">{busy ? "Saving…" : "Add cost"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
